import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { collection, query, orderBy, onSnapshot, addDoc, serverTimestamp, doc, getDoc, where } from 'firebase/firestore';
import { db } from './firebase';
import UserProfile from './UserProfile';
import config from '../config';
import './ChatInterface.css'; // Import the CSS file
import { Loading } from 'react-loading-dot'
import { loadStripe } from '@stripe/stripe-js';


interface Message {
    sender: 'user' | 'assistant';
    text: string;
}

interface ChatInterfaceProps {
    userId: string | null;
}

function isAxiosError(error: unknown): error is { response?: { data: unknown }; request?: unknown; message: unknown } {
    return !!error && typeof error === 'object' && ('response' in error || 'request' in error || 'message' in error);
}


const ChatInterface: React.FC<ChatInterfaceProps> = ({ userId }) => {
    const [messages, setMessages] = useState<Message[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [hasPaid, setHasPaid] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [isUserProfileCollapsed, setIsUserProfileCollapsed] = useState(true);
    const chatBodyRef = useRef<HTMLDivElement>(null); // Explicitly set the type to HTMLDivElement

    const [isLoading, setIsLoading] = useState(false); // State to control loading icon

    const stripePromise = loadStripe('pk_live_51KQICtKv1Ym6a69nqS6lcOj4DOuqfXLy3qHPrQub2RpF446uDl2JVqGB1SOKwlEdxURB25WRM9E6KV8jdJwLJ1WP00ryHDeqvb');

    const createOneTimePaymentSession = async () => {
        try {
            const response = await axios.post(`${config.apiUrl}/create-3-messages-checkout-session`, {
                userId: userId
            });
            if (response.status === 200) {
                const sessionId = response.data.id;
                const stripe = await stripePromise;
                if (stripe) {
                    stripe.redirectToCheckout({
                        sessionId: sessionId
                    }).then(function (result) {
                        if (result.error) {
                            console.error('Stripe redirect failed:', result.error.message);
                        }
                    });
                } else {
                    console.error('Stripe failed to initialize.');
                }
            }
        } catch (error: unknown) {
            if (isAxiosError(error)) {
                if (error.response) {
                    console.error('Server Response:', error.response.data);
                } else if (error.request) {
                    console.error('No Server Response:', error.request);
                } else {
                    console.error('Error:', error.message);
                }
            } else {
                console.error('An unknown error occurred:', error);
            }
        }


    };


    const createMonthlySubscriptionSession = async () => {
        try {
            const response = await axios.post(`${config.apiUrl}/create-monthly-subscription`, {
                userId: userId
            });
            if (response.status === 200) {
                const sessionId = response.data.id;
                const stripe = await stripePromise;
                if (stripe) {
                    stripe.redirectToCheckout({
                        sessionId: sessionId
                    }).then(function (result) {
                        if (result.error) {
                            console.error('Stripe redirect failed:', result.error.message);
                        }
                    });
                } else {
                    console.error('Stripe failed to initialize.');
                }
            }
        } catch (error: unknown) {
            console.error('Error creating monthly subscription session:', error);
            if (isAxiosError(error)) {
                if (error.response) {
                    console.error('Server Response:', error.response.data);
                } else if (error.request) {
                    console.error('No Server Response:', error.request);
                } else {
                    console.error('Error:', error.message);
                }
            } else {
                console.error('An unknown error occurred:', error);
            }
        }
    };

    useEffect(() => {
        // Reset zoom level to 1 when transitioning to this page
        document.querySelector('meta[name=viewport]')!.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0');
    }, []);


    const checkPaymentStatus = async () => {
        if (userId) {
            const userDoc = await getDoc(doc(db, 'users', userId));
            if (userDoc.exists()) {
                const userData = userDoc.data();
                console.log(userData?.messages);
                if (userData?.hasPaidSubscription ||
                    (userData?.hasPaidSingle && userData?.messages >= 1) ||
                    (userData?.messages >= 1)) {
                    setHasPaid(true);
                    setShowModal(false);
                } else {
                    setHasPaid(false);
                    setShowModal(true);
                }
                console.log(hasPaid);
            }
        }
    };

    useEffect(() => {
        const messagesCollection = collection(db, 'messages');
        const q = query(messagesCollection, orderBy('timestamp'), where('userId', '==', userId));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setMessages(snapshot.docs.map(doc => doc.data() as Message));
        });

        checkPaymentStatus();

        return unsubscribe;
    }, [userId]);


    useEffect(() => {
        if (chatBodyRef.current) {
            chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
        }
    }, [messages]); // Scroll to bottom whenever messages change


    // useEffect to show popup when hasPaid changes to false
    useEffect(() => {
        if (!hasPaid) {
            setShowModal(true);
        } else {
            setShowModal(false);
        }
    }, [hasPaid]);


    const handleSend = async () => {

        setIsLoading(true); // Show loading icon

        (async () => {
            await checkPaymentStatus()

            if (hasPaid && inputValue.trim()) {
                setShowModal(false);
                const newMessage: Message = { sender: 'user', text: inputValue.trim() };
                setMessages([...messages, newMessage]);
                setInputValue('');
                await addDoc(collection(db, 'messages'), {
                    ...newMessage,
                    timestamp: serverTimestamp(),
                    userId
                });
                try {
                    console.log("Handle Send Called"); // Add this
                    const response = await axios.post(`${config.apiUrl}/send-message`, { message: newMessage, userId: userId }, {
                        headers: {
                            'Content-Type': 'application/json',
                        }
                    });
                    if (response.status === 200) {

                        // No need to add the assistant's reply here.
                        // Firebase's real-time update will handle it.
                    }
                } catch (error: any) { // You can use 'any' or a more specific type
                    if (axios.isAxiosError(error)) { // Type guard to make sure it's an Axios error
                        console.error('Axios error details:', error.response?.data);
                    } else {
                        console.error('An unknown error occurred:', error);
                    }
                }
            } else {
                setShowModal(true);
            }

        })();
        console.log('has paid:', hasPaid);
        setIsLoading(false); // Show loading icon
    };

    return (
        <div>
            <h1> Chat with Astrologer </h1>
            <h4> Please fill out User Profile for best experience </h4>
            <button onClick={() => setIsUserProfileCollapsed(!isUserProfileCollapsed)}>
                {isUserProfileCollapsed ? "Expand User Profile" : "Collapse User Profile"}
            </button>
            <div className={`userProfile-container ${isUserProfileCollapsed ? 'collapsed' : ''}`}>
                <UserProfile userId={userId}></UserProfile>
            </div>
            <div className="chat-container">
                <div className="chat-header">
                    Chat with Astrologer
                </div>

                <div className="chat-body" ref={chatBodyRef}>
                    {messages.map((message, index) => (
                        <div key={index} className={`chat-message ${message.sender}`}>
                            {message.text}
                        </div>
                    ))}
                    {isLoading &&
                        <div style={{ position: 'absolute', bottom: '0', left: '0', zIndex: '1' }}>
                            <div style={{ position: 'static', top: 'auto', transform: 'none' }}>
                                <Loading margin="0.8rem" size="0.8rem" background="var(--text-color)" />
                            </div>
                        </div>} {/* Add loading icon */}
                </div>
                <div className="chat-footer">
                    <input
                        type="text"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        placeholder="Type your message..."
                    />
                    <button onClick={handleSend}>Send</button>
                </div>
                {showModal &&
                    <div className="modal">
                        <div className="modal-content">
                            <button className="close-button" onClick={() => setShowModal(false)}>X</button>
                            <div className="modal-header">
                                <h4>Please make a payment to continue using this service.</h4>
                            </div>
                            <div className="modal-actions">
                                <button onClick={createOneTimePaymentSession}>One-time payment of $1 for 3 messages</button>
                                <button onClick={createMonthlySubscriptionSession}>Monthly Subscription for unlimited messages</button>
                            </div>
                        </div>
                    </div>
                }

            </div>
        </div>
    );
};

export default ChatInterface;